import React, { ReactElement } from 'react'
import { Flex, Text, Box } from 'theme-ui'
import { Button, Card, Svg } from 'ui'
import { setSelectedFiltersPK, removeFilterPKFromSelected } from 'store/freeDNS'
import { useAppDispatch } from 'store/hooks'
import { navigate } from 'gatsby'
import { freeDnsPagePath } from 'components/FreeDns'
import ArrowRightIcon from 'images/freeDNS/arrow-right.svg'
import InfoIcon from 'images/freeDNS/info-filled.svg'
import CheckIcon from 'images/freeDNS/check.svg'
import { getOnKeyPressWith } from 'utils/getOnKeyPressWith'
import ExternalSvgIcon, { IconType } from 'components/Dashboard/CommonComponents/ExternalSvgIcon'

const CustomConfigurationItem = ({
  name,
  PK,
  isSelected,
  isPremium,
  openBlockList,
}: {
  name: string
  PK: string
  description: string
  isSelected: boolean
  isPremium?: boolean
  openBlockList: () => void
}): ReactElement => {
  const dispatch = useAppDispatch()

  const onItemClick = () => {
    if (isPremium) {
      return
    }

    !isSelected ? dispatch(setSelectedFiltersPK(PK)) : dispatch(removeFilterPKFromSelected(PK))
  }

  const onInfoIconClick = event => {
    openBlockList()
    event.stopPropagation()
  }

  return (
    <Card
      data-testid={`filter-${PK}`}
      sx={{
        height: '12rem',
        width: ['100%', '30rem'],
        p: 0,
        backgroundColor: isSelected ? 'successTransparent' : 'lightSkyBlue5',
        borderRadius: '1.2rem',
        cursor: !isPremium && 'pointer',
        alignSelf: 'left',
        border: '2px solid',
        borderColor: isSelected ? 'successOpaque' : 'transparent',
        '>.card-children': {
          height: '100%',
        },
        // hover, but not a tap on touch screen
        '@media (hover: hover)': {
          ':hover': {
            transform: 'scale(1.025)',
          },
        },
      }}
    >
      <Box
        sx={{
          p: '2.4rem',
          height: '100%',
        }}
        onClick={onItemClick}
        onKeyDown={getOnKeyPressWith({ callback: onItemClick })}
        tabIndex={isPremium ? -1 : 0}
      >
        <Flex
          sx={{
            height: '100%',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Flex
            sx={{
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <ExternalSvgIcon
              icon={PK}
              type={IconType.FILTERS}
              descriptiveText={`${name} filter icon`}
              fill={isSelected ? 'successOpaque' : 'lightSteelBlue'}
              sx={{
                width: '3.2rem',
                height: '3.2rem',
              }}
            />
            <Text
              variant="size17Weight600Line138"
              sx={{
                width: 'max-content',
                color: 'aliceBlue',
              }}
              tabIndex={0}
            >
              {name}
            </Text>
          </Flex>
          <Flex
            sx={{
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              flexDirection: 'column',
            }}
          >
            {isPremium ? (
              <Button
                variant="transparent"
                sx={{
                  border: 'none',
                  backgroundColor: 'transparent',
                  p: 0,
                  color: 'lightSteelBlue',
                  opacity: 0.5,
                  ':hover': {
                    opacity: 1,
                    color: 'aliceBlue',
                    'svg path': {
                      fill: 'aliceBlue',
                    },
                  },
                }}
                ariaLabel={`Upgrade to premium to use ${name} filter`}
                onClick={() => navigate('/plans', { state: { prevPage: freeDnsPagePath } })}
              >
                <Text variant="size17Weight600Line138" sx={{ mr: '0.8rem' }}>
                  Upgrade
                </Text>
                <Svg svg={ArrowRightIcon} descriptiveText="Navigate to upgrade page" fill="body" />
              </Button>
            ) : (
              <Flex
                sx={{
                  width: '2.4rem',
                  height: '2.4rem',
                  borderRadius: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isSelected ? 'successOpaque' : 'aliceBlue15',
                  mt: '0.4rem',
                }}
              >
                {isSelected && (
                  <Svg
                    svg={CheckIcon}
                    descriptiveText={`${name} filter selected`}
                    fill="blueShades"
                    sx={{
                      width: '2rem',
                      height: '2rem',
                    }}
                  />
                )}
              </Flex>
            )}
            <Svg
              data-testid="info-item-svg"
              svg={InfoIcon}
              descriptiveText={`View ${name} filter information`}
              fill={isSelected ? 'aliceBlue' : 'lightSteelBlue'}
              sx={{
                width: '2.4rem',
                height: '2.4rem',
                cursor: 'pointer',
                ':hover': {
                  path: {
                    fill: 'aliceBlue',
                  },
                },
              }}
              onClick={onInfoIconClick}
              onKeyDown={getOnKeyPressWith({ callback: onInfoIconClick })}
              tabIndex={0}
              aria-label={`Show ${name} filter info tray`}
              // Don't show focus border on click, but show it on keyboard navigation
              onMouseDown={event => event.preventDefault()}
            />
          </Flex>
        </Flex>
      </Box>
    </Card>
  )
}

export default CustomConfigurationItem
